import { useState, useCallback, useEffect } from 'react'
import { useSession } from '@/lib/auth-client'
import { walletAPI } from '@/lib/api/wallet'
import type { Invoice } from '@/lib/api/wallet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount)
}

interface StatusBadgeProps {
  status: string | null
}

function StatusBadge({ status }: StatusBadgeProps) {
  switch (status) {
    case 'paid':
      return <Badge variant="success">Paid</Badge>
    case 'open':
      return <Badge variant="outline">Open</Badge>
    case 'uncollectible':
      return <Badge variant="error">Uncollectible</Badge>
    case 'void':
      return <Badge variant="secondary">Void</Badge>
    case 'draft':
      return <Badge variant="secondary">Draft</Badge>
    default:
      return <Badge variant="secondary">{status ?? 'Unknown'}</Badge>
  }
}

export function BillingInvoiceList() {
  const session = useSession()
  const isAuthenticated = !!session.data?.user

  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchInvoices = useCallback(
    async (startingAfter?: string) => {
      if (!isAuthenticated) {
        setLoading(false)
        return
      }

      const isLoadMore = !!startingAfter
      if (isLoadMore) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      setError(null)

      try {
        const result = await walletAPI.getInvoices(isAuthenticated, {
          limit: 10,
          startingAfter,
        })
        if (isLoadMore) {
          setInvoices((prev) => [...prev, ...result.invoices])
        } else {
          setInvoices(result.invoices)
        }
        setHasMore(result.hasMore)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load invoices')
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [isAuthenticated]
  )

  useEffect(() => {
    void fetchInvoices()
    const refresh = () => { void fetchInvoices() }
    window.addEventListener('genie:billing-changed', refresh)
    return () => window.removeEventListener('genie:billing-changed', refresh)
  }, [fetchInvoices])

  const handleLoadMore = useCallback(() => {
    const lastInvoice = invoices[invoices.length - 1]
    if (lastInvoice) {
      fetchInvoices(lastInvoice.id)
    }
  }, [invoices, fetchInvoices])

  const handleRetry = useCallback(() => {
    setInvoices([])
    fetchInvoices()
  }, [fetchInvoices])

  // Loading state
  if (loading) {
    return (
      <div className="min-w-0 rounded-md border p-8 text-center text-muted-foreground">
        Loading invoices...
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-w-0 rounded-md border p-8 text-center">
        <p className="mb-3 text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" onClick={handleRetry}>
          <Icon name="refresh-cw" className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </div>
    )
  }

  // Empty state
  if (invoices.length === 0) {
    return (
      <div className="min-w-0 rounded-md border p-8 text-center text-muted-foreground">
        No invoices yet
      </div>
    )
  }

  return (
    <div className="min-w-0 rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[100px]">Date</TableHead>
            <TableHead className="min-w-[140px]">Description</TableHead>
            <TableHead className="min-w-[85px]">Amount</TableHead>
            <TableHead className="min-w-[85px]">Status</TableHead>
            <TableHead className="min-w-[85px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell className="font-normal text-muted-foreground">
                {formatDate(invoice.created)}
              </TableCell>
              <TableCell className="font-medium">
                {invoice.description || invoice.number || 'Invoice'}
              </TableCell>
              <TableCell className="font-medium">
                {formatAmount(invoice.amount, invoice.currency)}
              </TableCell>
              <TableCell>
                <StatusBadge status={invoice.status} />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {invoice.hostedInvoiceUrl && (
                    <Button variant="ghost" size="sm" asChild>
                      <a
                        aria-label="View invoice"
                        href={invoice.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon name="external-link" className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  {invoice.invoicePdf && (
                    <Button variant="ghost" size="sm" asChild>
                      <a
                        aria-label="Download invoice"
                        href={invoice.invoicePdf}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon name="download" className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {hasMore && (
        <div className="border-t p-3 text-center">
          <Button variant="ghost" size="sm" onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
